<script setup lang="ts">
import { computed, h } from 'vue'

import { Plus } from '@lucide/vue'
import { useQuery } from '@tanstack/vue-query'
import {
  type ColumnDef,
  createSortedRowModel,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  tableFeatures,
  useTable,
} from '@tanstack/vue-table'
import { RouterLink } from 'vue-router'

import { describeCron, fmtDateUtc } from '#operations/lib/cron-job'
import DataTable from '#shared/components/DataTable.vue'
import PageHeader from '#shared/components/PageHeader.vue'
import SortableHeader from '#shared/components/SortableHeader.vue'
import { chronosClient } from '#shared/services/api/chronos.client'
import type { components } from '#shared/services/api/chronos.schema'
import { Badge } from '#ui/badge'
import { Button } from '#ui/button'
import { ICONS } from '#utils/icons'
import { ROW_CLASS, ROW_LINK_CLASS } from '#utils/table'

type JobDto = components['schemas']['JobDto']

definePage({
  meta: { requiresAuth: true, title: 'Cron' },
})

const jobsQuery = useQuery({
  queryKey: ['cron-jobs'] as const,
  queryFn: async (): Promise<JobDto[]> => {
    const { data } = await chronosClient.GET('/api/admin/jobs')
    return data ?? []
  },
  refetchInterval: 30000,
})

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, datetime: sortFn_datetime },
})

const columns: ColumnDef<typeof features, JobDto>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) =>
      h(SortableHeader, {
        label: 'Name',
        sorted: column.getIsSorted(),
        onToggle: column.getToggleSortingHandler(),
      }),
    cell: ({ row }) =>
      h('div', { class: 'flex flex-col' }, [
        h(
          RouterLink,
          { to: `/cron/${row.original.id}`, class: `font-medium ${ROW_LINK_CLASS}` },
          () => row.original.name ?? '-',
        ),
        row.original.description
          ? h(
              'span',
              { class: 'line-clamp-1 text-xs text-muted-foreground' },
              row.original.description,
            )
          : null,
      ]),
  },
  {
    accessorKey: 'targetService',
    header: 'Target',
    cell: ({ row }) =>
      h('span', { class: 'text-sm text-muted-foreground' }, row.original.targetService ?? '—'),
  },
  {
    id: 'endpoint',
    header: 'Endpoint',
    cell: ({ row }) =>
      h('span', { class: 'font-mono text-xs' }, [
        h('span', { class: 'text-muted-foreground' }, `${row.original.httpMethod} `),
        row.original.endpointPath ?? '',
      ]),
  },
  {
    accessorKey: 'cronExpression',
    header: 'Schedule',
    cell: ({ row }) => {
      const expr = row.original.cronExpression
      if (!expr) return h('span', { class: 'text-sm text-muted-foreground' }, '—')
      const human = describeCron(expr)
      return h('div', { class: 'flex flex-col' }, [
        h('span', { class: 'text-sm' }, human ?? expr),
        human ? h('span', { class: 'font-mono text-xs text-muted-foreground' }, expr) : null,
      ])
    },
  },
  {
    accessorKey: 'isEnabled',
    header: 'Status',
    cell: ({ row }) =>
      h(Badge, { variant: row.original.isEnabled ? 'success' : 'secondary' }, () =>
        row.original.isEnabled ? 'Enabled' : 'Disabled',
      ),
  },
  {
    accessorKey: 'nextFireAtUtc',
    header: 'Next run',
    cell: ({ row }) => h('span', { class: 'text-sm' }, fmtDateUtc(row.original.nextFireAtUtc)),
  },
  {
    accessorKey: 'lastRunAtUtc',
    header: 'Last run',
    cell: ({ row }) => h('span', { class: 'text-sm' }, fmtDateUtc(row.original.lastRunAtUtc)),
  },
]

const table = useTable({
  features,
  data: computed(() => jobsQuery.data.value ?? []),
  columns,
})
</script>

<template>
  <PageHeader title="Cron" description="Scheduled jobs managed by Chronos.">
    <template #actions>
      <Button as-child>
        <RouterLink to="/cron/new">
          <Plus />
          New job
        </RouterLink>
      </Button>
    </template>
  </PageHeader>

  <DataTable
    :table="table"
    :is-loading="jobsQuery.isLoading.value"
    :error="jobsQuery.error.value"
    noun="jobs"
    :icon="ICONS.crons"
    :row-class="ROW_CLASS"
  >
    <template #empty-description>
      A cron job calls an endpoint on one of the backends on a schedule.
    </template>
    <template #empty-action>
      <Button as-child>
        <RouterLink to="/cron/new">
          <Plus />
          New job
        </RouterLink>
      </Button>
    </template>
  </DataTable>
</template>
